import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-bg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-btn-bg text-btn-text hover:bg-btn-bg-hover hover:text-btn-text-hover",
        destructive: "bg-red text-btn-text border border-btn-border hover:bg-red-light hover:text-btn-text-hover",
        outline: "bg-transparent text-btn-text border border-btn-border hover:bg-btn-bg-hover hover:text-btn-text-hover",
        secondary: "bg-bg-secondary text-white hover:bg-btn-bg-hover hover:text-btn-text-hover",
        ghost: "text-btn-text hover:bg-btn-bg-hover hover:text-btn-text-hover",
        link: "text-btn-text underline-offset-4 hover:underline",
      },
      size: {
        default: "min-w-[150px] h-10 px-4 py-2 rounded-[10px]",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
